import React from 'react';
import GenericHoc from 'src/common/generic-hoc';
import { HocOptions } from 'src/common/generic-hoc.types';
import { InvoiceDashboardStyles } from './InvoiceDashboard.styles';
import Navbar from 'src/common/Navbar/Navbar';
import { fetchInvoiceBucketMetricsData, fetchInvoiceData, setInvoiceFilters, setPagination } from './Invoices.actions';
import { Badge, Button, DatePicker, Input, message, Modal, Radio, Select, Table, Tabs, Tooltip } from 'antd';
import { dateFilterOptions, getRequiredInvoiceConfig, imageUploadOptions, InvoiceBucketLabelMap, invoiceSearchOptions, movementTypeFilterOptions, paginationOptions, pieceStatusPrettyNameMapping, podStatusFilterOptions, statusesPrettyNameMapping } from './Invoices.utils';
import { useDebounce } from 'src/library/hooks/useDebounce';
import moment from 'moment';
import SimplePaginator from 'src/common/SimplePaginator';
import VendorCodeFilter from 'src/components/vendorcodefilter/VendorCodeFilter';
import { EditOutlined, EyeOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import PodImagePreviewModal from 'src/common/PodImagePreviewModal';
import PodUpload from 'src/common/PodUpload';
import { uploadPodFiles } from '../Delivery/DeliveryAcknowledgement.utils';
import { uploadSinglePOD } from './Invoices.api';
import { GenericBulkUploadService } from 'src/components/GenericBulkUploadService';
import InvoiceUploadErrorModal from './InvoiceUploadErrorModal';
import { formatDateTime } from 'src/utils/utils';

const SEARCH_SANITIZE_REGEX = /[^A-Za-z0-9\-_/.]/g;

const InvoiceDashboard = (props: any) => {
  const {
    classes,
    fetchInvoiceData,
    setInvoiceFilters,
    bucketList = {},
    appliedFilters,
    invoicesData,
    isLoading,
    metricsData,
    fetchInvoiceBucketMetricsData,
    setPagination,
    pagination,
    consignmentActionConfig,
    t,
  } = props;

  const [searchValue, setSearchValue] = React.useState('');
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [selectedVendors, setSelectedVendors] = React.useState<any[]>([]);
  const [imageModalPreviewProps, setImageModalPreviewProps] = React.useState<{
    isVisible: boolean,
    imageList: Record<string, any>[],
  }>({
    isVisible: false,
    imageList: [],
  });

  const [podUploadModalParams, setPODUploadModalParams] = React.useState<{
    isVisible: boolean,
    selectedRows: any[],
    isUploading?: boolean,
    uploadedImageList?: any[],
    existingPodAction?: string,
    invoiceNumbersWithExistingPod?: any[],
    isBulk?: boolean,
    isErrorModalVisible?: boolean,
  }>({
    isVisible: false,
    selectedRows: [],
  });

  const [errorModalProps, setErrorModalProps] = React.useState<{
    isVisible: boolean,
    failureReason: string,
  }>({
    isVisible: false,
    failureReason: '',
  });

  const debouncedSearchValue = useDebounce(searchValue, 500);

  React.useEffect(() => {
    const searchBy = appliedFilters?.search?.searchBy;
    if (searchBy === 'tripReferenceNumber') {
      const stripped = (debouncedSearchValue || '').replace(SEARCH_SANITIZE_REGEX, '');
      if (stripped && stripped.length < 3) {
        return;
      }
      setInvoiceFilters({
        search: { ...(appliedFilters?.search || {}), searchValue: stripped },
      });
      return;
    }
    setInvoiceFilters({
      search: { ...(appliedFilters?.search || {}), searchValue: debouncedSearchValue },
    });
  }, [debouncedSearchValue]);

  React.useEffect(() => {
    fetchInvoiceBucketMetricsData();
    fetchInvoiceData();
  }, []);

  const handleRefresh = () => {
    fetchInvoiceBucketMetricsData();
    fetchInvoiceData();
  };

  React.useEffect(() => {
    if (invoicesData?.error) {
      message.error(invoicesData.error);
    }
  }, [invoicesData?.error]);

  React.useEffect(() => {
    if (metricsData?.error) {
      message.error(metricsData.error);
    }
  }, [metricsData?.error]);

  const getMinPODImages = (record) => {
    const requiredConfig = getRequiredInvoiceConfig(
      consignmentActionConfig?.flow_config,
      { consignmentData: record },
    );

    return requiredConfig?.[record?.status]?.min_proof_count ?? 1;
  };

  const renderNavbar = () => (
    <Navbar
      title={t("Approvals")}
    />
  );

  const onBucketChange = (activeTabKey) => {
    setInvoiceFilters({ bucket: activeTabKey });
  };

  const renderTabHeader = (key) => (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      {t(InvoiceBucketLabelMap[key].label) || key}
      <Badge
        count={metricsData?.data?.[key] || 0}
        showZero
        style={{
          marginLeft: '10px',
          width: '26px',
          backgroundColor: key === appliedFilters.bucket ? '#1659CB' : '#CCCCCC',
        }}
      />
    </div>
  );

  const uploadAndSubmitPOD = async (file) : Promise<any> => {
    const podUrl = await uploadPodFiles([file]);

    const params: any = {
      action_type: "bulk_upload_pod",
      proof_image_list: [...(podUrl || [])],
    };
    const response = await uploadSinglePOD(params);
    return response;
  };

  const handleSinglePODUpload = async (files, skipQRValidation) => {
    if (!files?.length) {
      message.error(t('Please select a file to upload'));
      return;
    }
    setPODUploadModalParams({
      ...podUploadModalParams,
      isUploading: true,
    });

    const existingPodImages = [];
    const newPodImages = [];
    let newPodImageUrls = [];

    files?.forEach((file) => {
      if (file.url) {
        existingPodImages.push({
          url: file.url,
          type: file.type,
        });
      } else {
        newPodImages.push(file);
      }
    });

    if (newPodImages.length) {
      newPodImageUrls = await uploadPodFiles(newPodImages);
    }

    const selectedRow = podUploadModalParams.selectedRows?.[0];
    const pieceStatus = selectedRow.piece_status;
    const pickupAction = ['pickup_completed', 'not_picked_up'];

    const response = await uploadSinglePOD({
      actionType: pickupAction.includes(pieceStatus) ? 'pickup' : 'delivery',
      status: pieceStatus,
      invoice_number: selectedRow.invoice_number,
      consignment_reference_number: selectedRow.consignment_number,
      reference_number: selectedRow.piece_reference_number,
      proof_image_list: [...(existingPodImages || []), ...(newPodImageUrls || [])],
      skip_qr_validation: skipQRValidation,
    });

    if (response?.isSuccess) {
      if (response?.data?.failureArray?.length) {
        setPODUploadModalParams({
          ...podUploadModalParams,
          uploadedImageList: [...(existingPodImages || []), ...(newPodImageUrls || [])],
          isUploading: false,
        });
        setErrorModalProps({
          isVisible: true,
          failureReason: response.data.failureArray[0].failure_reason,
        });
      } else {
        setPODUploadModalParams({
          isVisible: false,
          isUploading: false,
          selectedRows: [],
        });
        message.success(t('POD uploaded successfully'));
        handleRefresh();
      }
    } else {
      message.error(response.errorMessage);
      setPODUploadModalParams({
        ...podUploadModalParams,
        isUploading: false,
      });
    }
  };

  const renderErrorModal = () => {
    return (
      <InvoiceUploadErrorModal
        isVisible={errorModalProps.isVisible}
        failureReason={errorModalProps.failureReason}
        handleOk={async () => {
          setErrorModalProps({
            isVisible: false,
            failureReason: '',
          });
          await handleSinglePODUpload(podUploadModalParams.uploadedImageList, true);
        }}
        handleCancel={() => {
          setPODUploadModalParams({
            ...podUploadModalParams,
            isUploading: false,
          });
          setErrorModalProps({
            isVisible: false,
            failureReason: '',
          });
        }}
      />
    );
  };

  const handleBulkPODUpload = async (files) => {
    if (!files?.length) {
      message.error(t('Please select a file to upload'));
      return;
    }
    try {
      GenericBulkUploadService.uploadData({
        data: files,
        heading: t('Upload Bulk Invoice POD'),
        batchFunction: uploadAndSubmitPOD,
        failureArrayKeysToShow: ['invoice_number', 'failure_reason', 'proof_image_list'],
      });
      setPODUploadModalParams({
        isVisible: false,
        isUploading: false,
        selectedRows: [],
      });
      return;
    } catch (e) {
      message.error(e.toString());
    }
  };

  const beforeUpload = (file) => {
    const isLt1M = file.size / 1024 / 1024 < 1;
    if (!isLt1M) {
      message.error(t('File must smaller than 1 MB!'));
    }
    return isLt1M;
  };

  const renderPODUploadModal = () => {
    const selectedRow = podUploadModalParams.selectedRows?.[0];
    return (
      <PodUpload
        isVisible={podUploadModalParams.isVisible}
        isBulk={podUploadModalParams.isBulk}
        handleSubmit={podUploadModalParams.isBulk ? handleBulkPODUpload : handleSinglePODUpload}
        shouldSendExistingImages
        title={t("Upload POD")}
        loading={podUploadModalParams.isUploading}
        listType="picture-card"
        uploadedImageList={podUploadModalParams.uploadedImageList}
        onModalClose={() => {
          setPODUploadModalParams({
            isVisible: false,
            selectedRows: [],
          });
        }}
        beforeUpload={beforeUpload}
        consignmentData={selectedRow}
        pieceStatusData={{
          type: ['delivered', 'undelivered', 'attempted']?.includes(selectedRow?.status) ? 'delivery' : 'pickup',
          status: selectedRow?.status,
        }}
      />
    );
  };

  const renderImageModal = () => {
    if (!imageModalPreviewProps.imageList?.length) {
      return null;
    }
    return (
      <PodImagePreviewModal
        isVisible={imageModalPreviewProps.isVisible}
        handleClose={() => setImageModalPreviewProps({
          isVisible: false,
          imageList: [],
        })}
        imageList={imageModalPreviewProps.imageList}
      />
    );
  };

  const hideBulkUploadActions = () => {
    const requiredConfig = getRequiredInvoiceConfig(
      consignmentActionConfig?.flow_config,
      {},
    );
    return requiredConfig?.hide_bulk_upload_pod || false;
  };

  const renderPODUpload = (record) => {
    const proofImageList = record?.proof_image_list || [];
    const isDisabled = ['IN_REVIEW', 'APPROVED'].includes(record?.invoice_status);
    const minPodImages = getMinPODImages(record);
    const isUploadDisabled = isDisabled || minPodImages === 0;

    return (
      <div>
        {(proofImageList?.length) ? (
          <div style={{ display: 'flex' }}>
            <EyeOutlined
              className={classes.uploadIcon}
              onClick={() => setImageModalPreviewProps({
                isVisible: true,
                imageList: proofImageList || [],
              })}
              style={{ marginRight: '8px' }}
            />
            <EditOutlined
              className={isDisabled ? classes.uploadIconDisabled : classes.uploadIcon}
              onClick={() => {
                if (isDisabled) return;
                setPODUploadModalParams({
                  isVisible: true,
                  selectedRows: [record],
                  uploadedImageList: proofImageList,
                });
              }}
            />
          </div>
        ) :
          <UploadOutlined
            className={isUploadDisabled ? classes.uploadIconDisabled : classes.uploadIcon}
            onClick={() => {
              if (isUploadDisabled) return;
              setPODUploadModalParams({
                isVisible: true,
                selectedRows: [record],
              });
            }}
          />}
      </div>
    );
  };

  const renderBulkUploadButton = () => {
    return (
      <Button
        onClick={() => {
          setPODUploadModalParams({
            isVisible: true,
            selectedRows: [],
            isBulk: true,
          });
        }}
        className={classes.bulkUploadButton}
      >
        <UploadOutlined /> {t('Bulk Upload POD')}
      </Button>
    );
  };

  const generateColumnsToShow = () => {
    const columnData: {
      title: string;
      dataIndex: string;
      key: string;
      width: number;
    }[] = [];

    const bucketWiseColumnList = bucketList[appliedFilters.bucket];

    bucketWiseColumnList?.forEach((item) => {
      const itemToPush = {
        title: t(item.pretty_name),
        dataIndex: item.column_id,
        key: item.column_id,
        width: 150,
      };

      switch (item.column_id) {
        case 'invoice_status':
          itemToPush['render'] = (text) => t(statusesPrettyNameMapping[text]);
          break;

        case 'piece_status':
          itemToPush['render'] = (text) => t(pieceStatusPrettyNameMapping[text]) || text;
          break;

        case 'proof_image_list':
          itemToPush['render'] = (_, record) => renderPODUpload(record);
          break;
        case 'pod_raised_at':
        case 'pod_approved_at':
        case 'pod_rejected_at':
          itemToPush['render'] = (text) => {
            return <div>{text ? formatDateTime(text) : null}</div>;
          };
          break;
        case 'movement_type':
          itemToPush['render'] = (text) => t(text);
          break;
        case 'trip_number':
          itemToPush['render'] = (text) => (text || '—');
          break;
        default:
          itemToPush['render'] = (_, record) => _;
          break;
      }

      columnData.push(itemToPush);
    });

    return columnData;
  };

  const rowSelection = {
    selectedRows,
    onChange: (selectedRows: React.Key[]) => {
      setSelectedRows(selectedRows);
    },
    getCheckboxProps: (record) => ({
      disabled: record.invoice_status === 'APPROVED',
    }),
  };

  const renderTable = (key) => {
    return (
      <Table
        rowKey={(record) => record['id']}
        dataSource={invoicesData.data || []}
        columns={generateColumnsToShow()}
        // rowSelection={rowSelection}
        loading={isLoading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        className={classes.tableParent}
        locale={{ emptyText: t('No data found') }}
      />
    );
  };

  const renderSearchNumber = (): React.ReactElement => {
    const searchBy = appliedFilters?.search?.searchBy;
    let placeholder = t('Search Invoice Number');
    if (searchBy === 'consignmentReferenceNumber') {
      placeholder = t('Search Consignment Number');
    } else if (searchBy === 'tripReferenceNumber') {
      placeholder = t('Search Trip Reference Number');
    }
    return (
      <div className={classes.searchContainer}>
        <Select
          style={{ width: '200px' }}
          value={searchBy}
          onChange={(value) => {
            setSearchValue('');
            setInvoiceFilters({
              search: { searchBy: value, searchValue: '' },
            });
          }}
          options={invoiceSearchOptions.map((option) => ({
            ...option,
            label: t(option.label),
          }))}
          className={classes.filterSelect}
        />
        <Input.Search
          style={{ width: '250px' }}
          placeholder={`${placeholder}`}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className={classes.inputSearch}
        />
      </div>
    );
  };

  const renderMovementTypeFilter = () => {
    return (
      <div className={classes.filterDiv}>
        <div className={classes.filterTitle}>{t('Movement Type')}</div>
        <Select
          style={{ width: 120 }}
          value={appliedFilters?.movementType}
          onChange={(value) => {
            setInvoiceFilters({ movementType: value });
          }}
          allowClear
          options={movementTypeFilterOptions.map((option) => ({
            ...option,
            label: t(option.label),
          }))}
          placeholder={t("Select")}
          className={classes.filterOptionSelect}
        />
      </div>
    );
  };

  const renderPODStatusFilter = () => {
    return (
      <div className={classes.filterDiv}>
        <div className={classes.filterTitle}>{t('POD Status')}</div>
        <Select
          style={{ width: 150 }}
          value={appliedFilters?.invoiceStatus}
          onChange={(value) => {
            setInvoiceFilters({ invoiceStatus: value });
          }}
          allowClear
          options={podStatusFilterOptions.map((option) => ({
            ...option,
            label: t(option.label),
          }))}
          placeholder={t("Select")}
          className={classes.filterOptionSelect}
        />
      </div>
    );
  };

  const handleVendorFilterChange = (value: any) => {
    const vendors = Array.isArray(value) ? value : [];
    setSelectedVendors(vendors);
    const vendorIds = vendors.map((item) => item.value as string);
    setInvoiceFilters({ vendorIdsToFilter: vendorIds });
  };

  const renderVendorCodeFilter = () => {
    return (
      <div className={classes.filterDiv}>
        <div className={classes.filterTitle}>{t('Vendor Code')}</div>
        <VendorCodeFilter
          value={selectedVendors}
          onChange={handleVendorFilterChange}
          placeholder={t('Vendor Code')}
          mode="multiple"
          size="middle"
          style={{ width: 200 }}
          className={classes.filterOptionSelect}
        />
      </div>
    );
  };

  const renderDateFilter = () => {
    const appliedTimeFilter = appliedFilters?.timeFilters?.[0];
    const value = appliedTimeFilter?.fromDateString && appliedTimeFilter?.toDateString ?
      [
        moment(appliedTimeFilter?.fromDateString),
        moment(appliedTimeFilter?.toDateString),
      ] :
      [moment().subtract(29, 'day'), moment()];

    return (
      <div className={classes.filterDiv}>
        <Select
          style={{ width: '120px' }}
          value={appliedFilters?.timeFilters?.[0]?.columnName}
          onChange={(value) => {
            setInvoiceFilters({
              timeFilters: [{ ...(appliedFilters?.timeFilters?.[0] || []), columnName: value }],
            });
          }}
          className={classes.filterSelect}
          options={dateFilterOptions.map((option) => ({
            ...option,
            label: t(option.label),
          }))}
        />
        <DatePicker.RangePicker
          format="DD/MM/YYYY"
          style={{ width: '220px' }}
          value={value as [moment.Moment, moment.Moment] | undefined}
          onChange={(value) => {
            setInvoiceFilters({
              timeFilters: [{
                ...(appliedFilters?.timeFilters?.[0] || []),
                fromDateString: value?.[0]?.format('YYYY-MM-DD'),
                toDateString: value?.[1]?.format('YYYY-MM-DD'),
              }],
            });
          }}
          allowClear={false}
          className={classes.dateFilter}
          ranges={{
            [t('Today')]: [moment(), moment()],
            [t('Last 7 days')]: [moment().subtract(6, 'day'), moment()],
            [t('Last 30 days')]: [moment().subtract(29, 'day'), moment()],
            [t('Last 60 days')]: [moment().subtract(59, 'day'), moment()],
            [t('This Month')]: [moment().startOf('month'), moment().endOf('month')],
          }}
          disabledDate={(current) => current && current.isAfter(moment().endOf('day'))}
        />
      </div>
    );
  };

  const renderPagination = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <SimplePaginator
          loading={isLoading}
          onFirstClick={() => setPagination({ pageNumber: 1 })}
          onPrevClick={() => setPagination({ pageNumber: pagination.pageNumber - 1 })}
          onNextClick={() => setPagination({ pageNumber: pagination.pageNumber + 1 })}
          isNext={pagination?.isNextPresent}
          pageNumber={pagination.pageNumber}
        />
        <Select
          className={classes.selectWithoutBorder}
          onChange={(resultPerPage) => setPagination({ resultPerPage })}
          value={pagination.resultPerPage}
        >
          {paginationOptions.map((option) => (
            <Select.Option value={option}>{t(`${option}/page`)}</Select.Option>
          ))}
        </Select>
      </div>
    );
  };

  const renderRefresh = () => {
    return (
      <Tooltip title={t("Refresh")}>
        <Button
          onClick={() => handleRefresh()}
        >
          <ReloadOutlined />
        </Button>
      </Tooltip>
    );
  };

  const renderFilters = () => {
    return (
      <div className={classes.filterCard}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', flex: 1 }}>
          {renderDateFilter()}
          {renderMovementTypeFilter()}
          {renderPODStatusFilter()}
          {renderVendorCodeFilter()}
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 }}>
          {!hideBulkUploadActions() ? renderBulkUploadButton() : null}
          {renderRefresh()}
          {renderPagination()}
        </div>
      </div>
    );
  };

  return (
    <div className={classes.mainDiv}>
      {renderNavbar()}
      <div className={classes.pageContent}>
        {renderSearchNumber()}
        <div className={classes.tabsContainer}>
          <Tabs type="card" onTabClick={onBucketChange}>
            {Object.keys(bucketList).map((key) => (
              <Tabs.TabPane className={classes.tabPane} tab={renderTabHeader(key)} key={key}>
                {renderFilters()}
                {renderTable(key)}
              </Tabs.TabPane>
            ))}
          </Tabs>
        </div>
      </div>
      {imageModalPreviewProps.isVisible && renderImageModal()}
      {podUploadModalParams?.isVisible && renderPODUploadModal()}
      {errorModalProps?.isVisible && renderErrorModal()}
    </div>
  );
};

const mapStateToProps = ({ masterData, invoices }) => ({
  invoicesData: invoices.invoicesData,
  bucketList: masterData.invoice_column_list_by_bucket,
  columnList: masterData.invoice_column_list,
  appliedFilters: invoices.filters,
  isLoading: invoices.isLoading,
  metricsData: invoices.metricsData,
  pagination: invoices.pagination,
  consignmentActionConfig: masterData.vendor_config?.consignment_action_config || {},
});

const mapDispatchToProps = (dispatch) => {
  return {
    fetchInvoiceData: (data) => dispatch(fetchInvoiceData(data)),
    setInvoiceFilters: (data) => dispatch(setInvoiceFilters(data)),
    fetchInvoiceBucketMetricsData: (data) => dispatch(fetchInvoiceBucketMetricsData(data)),
    setPagination: (data) => dispatch(setPagination(data)),
  };
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: InvoiceDashboardStyles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(InvoiceDashboard);
